/**
 * Persistence for the mobile Calendar | Chats tab. The choice is deliberately
 * durable (plain localStorage, no TTL): leaving the app on Chats means
 * reopening it on Chats, until the user switches or the session is reset
 * (account switch removes this key via `clearClientState`; logout wipes it
 * with everything else).
 */
export const MOBILE_NAV_TAB_STORAGE_KEY = "mobile_nav_tab";

export type StoredMobileNavTab = "chats" | "calendar";

export function readStoredMobileNavTab(): StoredMobileNavTab | null {
  try {
    const raw = localStorage.getItem(MOBILE_NAV_TAB_STORAGE_KEY);
    return raw === "chats" || raw === "calendar" ? raw : null;
  } catch {
    return null;
  }
}

export function storeMobileNavTab(tab: StoredMobileNavTab): void {
  try {
    localStorage.setItem(MOBILE_NAV_TAB_STORAGE_KEY, tab);
  } catch {
    // Private-mode / quota failures: the choice just doesn't stick.
  }
}

/**
 * What the thread list was doing when this session last rendered the shell.
 *
 * Module memory rather than storage, so a full page load falls back to the
 * stored choice below: it exists so that returning from a thread restores what
 * you left, which is a fact about this visit and not a preference.
 *
 * It lives here rather than in the shell that writes it because a screen outside
 * the shell has to be able to ask for the list before navigating back into it -
 * the shell reads this on mount, and by then it is too late to tell it anything.
 */
let rememberedThreadListOpen: boolean | undefined;

/** Records the choice for the next {@link resolveInitialThreadListOpen}. */
export function rememberThreadListOpen(open: boolean): void {
  rememberedThreadListOpen = open;
}

/** The remembered choice, or undefined when nothing has set one this load. */
export function readRememberedThreadListOpen(): boolean | undefined {
  return rememberedThreadListOpen;
}

/**
 * Resolves whether a fresh Calendar mount should open with the thread list
 * showing.
 *
 * Precedence: in-app memory (returning from a thread restores what you left) →
 * the persisted mobile choice → the default, which is the thread list on both
 * breakpoints (Chats on mobile, the sidebar on desktop).
 */
export function resolveInitialThreadListOpen({
  remembered,
  isMobileWidth,
  storedMobileTab,
}: {
  remembered: boolean | undefined;
  isMobileWidth: boolean;
  storedMobileTab: StoredMobileNavTab | null;
}): boolean {
  if (remembered !== undefined) return remembered;
  if (isMobileWidth) return storedMobileTab !== "calendar";
  return true;
}
